<template>
  <div class="map-select-point">
    <GmapMap
      id="map"
      ref="map-ref"
      map-type-id="roadmap"
      class="mc-map"
      :center="center"
      :zoom="zoom"
      :options="mapOptions"
      :style="{ height: '100%', width: '100%' }"
      @dragend="($event) => handleSelectPoint('marker', $event)"
      @center_changed="handleChangeCenter"
    >
      <PointMarker :marker="pointLatLng" :isDragging="isLoading" />
    </GmapMap>

    <div class="map-select-point__address-input-container">
      <AddressInput
        fixed
        :map="map"
        :modelValue="computedAddressValue"
        :optionType="optionType"
        @update:modelValue="($event) => handleSelectPoint('search', $event)"
      />
    </div>

    <div v-if="modelValue?.fullName" class="map-select-point__address">
      <div class="__icon">
        <i class="icon-outline_pin" />
      </div>

      <div class="__fullname" :class="{ __loading: isLoading }">
        <template v-if="isLoading">
          <b-skeleton width="100%" />
          <b-skeleton width="80%" />
        </template>

        <template v-else>
          {{ modelValue.fullName }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import { debounce } from '@/helpers/debounce'

import AddressInput from './AddressInput'
import PointMarker from './PointMarker'

import { GetAddressData } from '@/services/api/map'
import { GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE } from '../../lib/constants/types'
import setMapOptions from '../../lib/constants/map-options'

const initCenter = { lat: -2.958881, lng: 109.5543666 }

export default {
  components: {
    AddressInput,
    PointMarker,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    optionType: {
      type: Array,
      default: () => [GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE],
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      GOOGLE_MAP_ADDRESS_TYPE,
      MASTER_DATA_ADDRESS_TYPE,

      isLoading: false,
      isFromSearch: true,
      map: null,
      center: { ...initCenter },
      pointLatLng: { ...initCenter },
      zoom: 5,

      mapOptions: {},
    }
  },
  computed: {
    google: gmapApi,
    computedAddressValue() {
      if (this.isFromSearch && !this.isFromMarker) {
        return this.modelValue
      }

      return {}
    },
    isFromMarker() {
      return this.modelValue?.type === GOOGLE_MAP_ADDRESS_TYPE && !this.modelValue?.id
    },
  },
  async mounted() {
    await this.initMap()
  },
  methods: {
    async initMap() {
      if (this.map !== null) return

      const mapRef = this.$refs['map-ref']
      const self = this

      await mapRef.$mapPromise.then((map) => {
        this.map = map
        self.mapOptions = setMapOptions(self.google)
      })

      await this.$nextTick()

      this.pointLatLng = {
        lat: this.modelValue.lat,
        lng: this.modelValue.lng,
      }

      this.setCenter({ ...this.pointLatLng })
    },

    setCenter({ lat, lng, zoom = 15 }) {
      this.center = { lat, lng }
      this.zoom = zoom
    },

    handleChangeCenter(newCenter) {
      this.isLoading = true

      const newMarker = { lat: newCenter.lat(), lng: newCenter.lng() }
      this.pointLatLng = { ...newMarker }
    },

    handleSelectPoint: debounce(async function (type, data) {
      if (type === 'marker') {
        const address = await GetAddressData(this.pointLatLng.lat, this.pointLatLng.lng)

        this.$emit('update:modelValue', {
          type: GOOGLE_MAP_ADDRESS_TYPE,
          name: address.display_name,
          fullName: address.display_name,
          lat: address.lat,
          lng: address.lon,
        })

        this.isFromSearch = false
      } else if (type === 'search') {
        this.pointLatLng = { lat: data.lat, lng: data.lng }
        this.setCenter({ lat: data.lat, lng: data.lng })

        this.$emit('update:modelValue', { ...data })
        this.isFromSearch = true
      }

      this.isLoading = false
    }, 500),
  },
}
</script>

<style lang="scss" scoped>
.map-select-point {
  position: relative;
  border: 1px solid $teal-100;
  border-radius: 12px;
  overflow: hidden;
  height: 300px;

  :deep(.gm-ui-hover-effect) > span {
    background-color: #000;
  }

  &__address-input-container {
    padding: 10px;
    width: 100%;
    max-width: 400px;
    position: absolute;
    top: 0;
  }

  &__address {
    position: absolute;
    bottom: 0;
    height: 52px;
    width: 100%;
    padding: 8px 16px;
    background-color: $white;
    display: flex;
    gap: 16px;

    .__icon {
      min-width: 36px;
      max-height: 36px;
      border-radius: 36px;
      background-color: $cyan-100;
      color: $mep-color-text-400;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 20px;
      }
    }

    .__fullname {
      flex-grow: 1;
      color: $mep-color-text-400;
      font-size: 12px;

      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      
      &.__loading {
        display: flex;
        flex-direction: column;
      }
    }
  }
}
</style>
